import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Set } from 'immutable';
import { flatten, uniq } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LandingPage } from 'src/app/shared/authentication/authentication-api/authentication-client.model';
import { TcsPermission } from 'src/app/shared/authorization/permissions';
import {
  EmployeeType,
  EntityType,
  TimeType,
  UserRole,
} from 'src/graphql/generated/graphql.generated';
import { PermissionsService } from 'src/lib/amg-authorization/permissions.service';
import { AppState } from 'src/store/appstate';
import {
  AccessibleItemProps,
  selectAccessibleItemEntitiesByPersonId,
} from 'src/store/generated/accessible-item-record.generated';
import { PersonEntityFetchService } from 'src/store/generated/client-sync.generated';
import {
  PersonProps,
  PersonRecord,
  PersonViewModelBase,
} from 'src/store/generated/person-record.generated';
import { undefinedOrSwitchMap } from 'src/util/rxjs/operators';
const systemPersonRecord = PersonRecord.fromJS({
  id: 'fake-system-id',
  firstName: 'System',
  lastName: '',
  email: 'system.user@amgh.us',
  title: null,
  employeeId: null,
  employeeType: EmployeeType.Unspecified,
  timeType: TimeType.Unspecified,
  hireDate: null,
  terminationDate: null,
  departmentId: null,
  reportsToPersonId: null,
  activateOktaAccount: false,
  baseId: null,
  locationId: null,
  activeDirectoryId: null,
  companyId: 'fake-company-id',
  programId: null,
  notificationAddress: null,
  dateLastUpdated: new Date().getTime(),
  dateCreated: new Date().getTime(),
  dateDeleted: null,
  userRoles: [],
  allCompaniesAccessible: null,
  allBasesAccessible: null,
  allTrmProgramsAccessible: null,
  allCommunicationCentersAccessible: null,
  faaCompanyId: null,
  dispatchCompanyId: null,
  aviationCompanyId: null,
  isOutOfFlightCompliance: false,
  lastUpdatedById: null,
  outOfFlightComplianceComment: null,
  primaryCommunicationCenterId: null,
  grantTcsAccess: true,
  dateLastUpdatedFromGMRWorkday: null,
  appSettings: null,
  additionalCompanies: null,
});

export class PersonViewModel extends PersonViewModelBase {
  public static readonly systemUser = new PersonViewModel(systemPersonRecord, []);
  public readonly userRolesAllScopes: UserRole[];
  public readonly accessibleItems: AccessibleItemProps[];
  public readonly accessibleCompanies: string[];

  public readonly gravatar: string;
  public readonly name: string | null;

  get initials() {
    return `${this.firstName.charAt(0)} ${this.lastName.charAt(0)}`;
  }

  constructor(person: PersonProps, accessibleItems: AccessibleItemProps[]) {
    super(person);
    accessibleItems.forEach((ai) => {
      if (ai.personId !== this.id) {
        throw new Error(
          `PersonViewModel: accessible item ${ai.id} is not assigned to Person ${this.id}`,
        );
      }
    });
    this.accessibleItems = accessibleItems;

    this.accessibleCompanies = uniq(
      accessibleItems
        .filter((ai) => ai.type === EntityType.Company)
        .map((ai) => ai.entityId)
        .concat([this.companyId]),
    );

    this.name = `${person.firstName} ${person.lastName}`.trim();

    this.gravatar =
      person.id == null
        ? 'mm'
        : encodeURIComponent(
            `https://ui-avatars.com/api/${person.firstName}+${person.lastName}/48/222/FFF`,
          );

    this.userRolesAllScopes = uniq(
      flatten(
        accessibleItems
          .filter((ai) => ai.userRoles != null)
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          .map((ai) => ai.userRoles!.toArray())
          .concat(this._props.userRoles?.toArray() ?? []),
      ),
    );
  }

  get isPilot() {
    return this.userRoles.includes(UserRole.Pilot);
  }

  get userRoles(): Set<UserRole> {
    if (this._props.userRoles == null) {
      throw new Error(
        `User roles are null for user ${this.firstName} ${this.lastName} (id: ${this.id}, employeeId: ${this.employeeId})`,
      );
    }
    return this._props.userRoles;
  }

  public hasAccessibleCompany(companyId: string) {
    return this.allCompaniesAccessible === true || this.accessibleCompanies.includes(companyId);
  }

  public hasGlobalRole(...roles: UserRole[]): boolean {
    return this.userRoles.find((r) => roles.includes(r)) != null;
  }

  public hasRoleAnyScope(...roles: UserRole[]): boolean {
    return this.userRolesAllScopes.find((r) => roles.includes(r)) != null;
  }

  public hasAnyRoleAnyScope(roles: UserRole[]): boolean {
    for (const role of roles) {
      if (this.hasRoleAnyScope(role)) {
        return true;
      }
    }
    return false;
  }

  public hasAnyGlobalRole(roles: UserRole[]): boolean {
    for (const role of roles) {
      if (this.hasGlobalRole(role)) {
        return true;
      }
    }
    return false;
  }

  public hasPermissionAnyGlobalRole(
    permissionsService: PermissionsService,
    permission: TcsPermission,
  ) {
    return permissionsService.hasPermission(this.userRoles.toArray(), permission);
  }

  public hasPermissionAnyScope(permissionsService: PermissionsService, permission: TcsPermission) {
    return permissionsService.hasPermission(this.userRolesAllScopes, permission);
  }

  public hasAnyPermissionAnyScope(
    permissionsService: PermissionsService,
    permissions: TcsPermission[],
  ) {
    return permissionsService.hasAnyPermission(this.userRolesAllScopes, permissions);
  }
}

export class UserViewModel extends PersonViewModel {
  constructor(
    personRecord: PersonProps,
    public readonly companyCode: string,
    accessibleItems: AccessibleItemProps[],
  ) {
    super(personRecord, accessibleItems);
  }

  get landingPage(): LandingPage {
    if (
      this.userRoles.includes(UserRole.Communications) ||
      this.userRoles.includes(UserRole.CommunicationsSupervisor)
    ) {
      return LandingPage.Ops;
    }

    if (this.userRoles.includes(UserRole.Executive) || this.userRoles.includes(UserRole.External)) {
      return LandingPage.FleetStatus;
    }

    return LandingPage.Insights;
  }
}

@Injectable({ providedIn: 'root' })
export class PersonViewModelService {
  constructor(private _people: PersonEntityFetchService, private _store: Store<AppState>) {}

  public getById(id: string): Observable<PersonViewModel | undefined> {
    return this._people
      .getById(id)
      .pipe(
        undefinedOrSwitchMap((p) =>
          this._store
            .select(selectAccessibleItemEntitiesByPersonId(id))
            .pipe(map((ai) => new PersonViewModel(p, ai.toArray()))),
        ),
      );
  }
}
